import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";

import { seenResourceUrisAtom } from "../atoms";
import { extractResourceUrisFromMessages } from "../chat.helpers";
import type { ExtendedUIMessage } from "../chat.types";

export interface UseChatResourceLinkTrackingOptions {
  sessionId: string;
  initialMessages: ExtendedUIMessage[];
  status: "streaming" | "submitted" | "ready" | "error";
}

export interface UseChatResourceLinkTrackingReturn {
  /**
   * Marks a resource URI as seen and returns whether onFirstSeen should be called.
   * Returns true only if: the URI hasn't been seen before AND status is streaming.
   * This state persists across ChatProvider unmount/remount cycles via Jotai atom.
   */
  markResourceSeen: (uri: string) => boolean;
}

/**
 * Hook to track which resource URIs have been "seen" (had onFirstSeen called).
 * Persists state via Jotai atom to survive ChatProvider unmount/remount cycles.
 */
export const useChatResourceLinkTracking = ({
  sessionId,
  initialMessages,
  status,
}: UseChatResourceLinkTrackingOptions): UseChatResourceLinkTrackingReturn => {
  const [seenResourceUris, setSeenResourceUris] = useAtom(seenResourceUrisAtom);

  // Compute initial URIs synchronously during render to avoid race condition.
  // Child component effects run before parent effects, so we need this available
  // immediately for markResourceSeen to check against.
  const initialResourceUris = useMemo(() => extractResourceUrisFromMessages(initialMessages), [initialMessages]);

  // Persist initial URIs to the atom (for future unmount/remount cycles).
  // This effect runs after child effects, but that's OK because markResourceSeen
  // checks against initialResourceUris synchronously.
  useEffect(() => {
    if (initialResourceUris.size > 0) {
      setSeenResourceUris((prev) => {
        const existingUris = prev[sessionId] ?? new Set<string>();
        const mergedUris = new Set([...existingUris, ...initialResourceUris]);

        // Only update if there are new URIs to add
        if (mergedUris.size === existingUris.size) {
          return prev;
        }

        return {
          ...prev,
          [sessionId]: mergedUris,
        };
      });
    }
  }, [sessionId, initialResourceUris, setSeenResourceUris]);

  // Marks a resource URI as seen and returns whether onFirstSeen should be called.
  // Returns true only if the URI hasn't been seen AND status is streaming.
  const markResourceSeen = useCallback(
    (uri: string): boolean => {
      const sessionUris = seenResourceUris[sessionId] ?? new Set<string>();

      // Check against both the atom (persisted) and initialResourceUris (synchronous).
      // This handles the race condition where child effects run before the seeding effect.
      if (sessionUris.has(uri) || initialResourceUris.has(uri)) {
        return false;
      }

      // Mark as seen
      setSeenResourceUris((prev) => ({
        ...prev,
        [sessionId]: new Set([...(prev[sessionId] ?? []), uri]),
      }));

      // Only call onFirstSeen for resources that appear during active streaming
      return status === "streaming";
    },
    [sessionId, seenResourceUris, initialResourceUris, setSeenResourceUris, status]
  );

  return { markResourceSeen };
};
